import { CLADDING_RSI, getSteelSpacingGroupOptions } from './constants';
import {
  AIR_FILM_INTERIOR_RSI,
  AIR_FILM_EXTERIOR_RSI,
  INTERIOR_GWB_RSI_PER_MM,
  roundOff,
  toR,
  OSB_RSI_PER_MM,
  CODEBORD_RSI_PER_INCH,
  BRICK_AIR_SPACE_RSI,
  RAINSCREEN_RSI,
  RAINBARRIER_RSI_PER_INCH,
  getFramingText,
} from '../shared/constants';

const layerListFromInputsSteel = (state, result, t, locale) => {
  const { code } = locale || {};
  const rows = [];

  // Always present
  rows.push({
    id: 'film_ext',
    label: t('thermal_calculator.walls_above_grade.exterior_air_film'),
    rsi: AIR_FILM_EXTERIOR_RSI,
  });

  // Cladding
  if (state.cladding === 'brickClay102') {
    rows.push({
      id: 'clad',
      label: t('thermal_calculator.walls_above_grade.cladding_brick'),
      rsi: CLADDING_RSI[state.cladding],
    });
    if (state.hasBrickAirSpace) {
      // eslint-disable-next-line max-len
      rows.push({
        id: 'brick_air_gap',
        label: `25 mm (1${t('thermal_calculator.inches')}) ${t('thermal_calculator.walls_above_grade.brick_air_gap')}`,
        rsi: result.brickAirSpaceRSI,
      });
    }
  } else {
    rows.push({
      id: 'clad',
      label: t('thermal_calculator.walls_above_grade.other_cladding'),
      rsi: CLADDING_RSI[state.cladding] ?? 0,
    });
  }

  if (result.rainScreenRSI > 0) {
    rows.push({
      id: 'rainscreen',
      label: t('thermal_calculator.walls_above_grade.steel_rainscreen_rsi'),
      rsi: result.rainScreenRSI,
    });
  }

  rows.push({ id: 'sheathing', label: t('thermal_calculator.walls_above_grade.wb'), rsi: 0 });

  // Sheathing pieces (show exactly what the user selected)
  if (result.sheathingType === 'osb') {
    const calc = parseFloat(result.osbThickness || '0');
    rows.push({
      id: 'osb',
      label: `${t('thermal_calculator.walls_above_grade.osb_exterior_sheathing')} ${new Intl.NumberFormat(code).format(
        calc
      )} mm`,
      rsi: OSB_RSI_PER_MM * calc,
    });
  }
  if (result.sheathingType === 'gwb') {
    const calc = parseFloat(result.gwbSheathThickness || '0');
    rows.push({
      id: 'gwbSheath',
      label: `${t('thermal_calculator.walls_above_grade.gwb_exterior')} ${new Intl.NumberFormat(code).format(calc)} mm`,
      rsi: INTERIOR_GWB_RSI_PER_MM * calc,
    });
  }
  if (result.sheathingType === 'codebord') {
    const calc = parseFloat(result.codebordSheathThickness || '0');
    rows.push({
      id: 'codebordSheath',
      label: `${t('thermal_calculator.walls_above_grade.codebord_sheathing_exterior')} ${new Intl.NumberFormat(
        code
      ).format(calc)} ${t('thermal_calculator.inches')}`,
      rsi: CODEBORD_RSI_PER_INCH * calc,
    });
  }

  if (result.exteriorInsulationRSI > 0) {
    const extLabel =
      state.exteriorInsulationType === 'rainbarrier'
        ? `${t('thermal_calculator.walls_above_grade.exterior_insulation_rainbarrier')} ${new Intl.NumberFormat(
            code
          ).format(state.exteriorRainbarrierInches)}${t('thermal_calculator.inches')}`
        : `${t('thermal_calculator.walls_above_grade.exterior_insulation_codebord')} ${new Intl.NumberFormat(
            code
          ).format(state.exteriorCodebordInches)}${t('thermal_calculator.inches')}`;
    rows.push({ id: 'ext_insul', label: extLabel, rsi: result.exteriorInsulationRSI });
  }

  rows.push({ id: 'vb', label: t('thermal_calculator.floors_over_unheated_spaces.vapour_retarder'), rsi: 0 });

  if (state.woodStrapBehindGWB) {
    rows.push({ id: 'strap', label: t('thermal_calculator.walls_above_grade.steel_strapping_air_cavity'), rsi: 0.16 });
  }

  if (result.interiorGwbRSI > 0) {
    rows.push({
      id: 'int_gwb',
      label: `${t('thermal_calculator.floors_over_unheated_spaces.gypsum_board')} ${new Intl.NumberFormat(code).format(
        state.interiorGWB
      )} mm`,
      rsi: result.interiorGwbRSI,
    });
  }

  // Interior film
  rows.push({
    id: 'film_int',
    label: t('thermal_calculator.walls_above_grade.interior_air_film'),
    rsi: AIR_FILM_INTERIOR_RSI,
  });

  return rows;
};

// --- Af% selection per old sheet (percent, not fraction)
const structurePercent = spacingGroup => (spacingGroup === '>=500' ? 0.67 : 0.77); // 24" -> 0.67, else 0.77

// --- K1 selection
export const pickK1 = state => {
  const spacingIs24 = state.steelSpacingGroup === '>=500';

  if (spacingIs24) return 0.5;

  // Only give 0.4 when exterior CI exists
  if (state.hasExteriorInsulation) return 0.4;

  return 0.33;
};

// --- Utility: sheathing RSI (OSB/GWB/Codebord)
const sheathingRSI = state => {
  const osb = state.sheathingType === 'osb' ? OSB_RSI_PER_MM * parseFloat(state.osbThickness || '0') : 0;

  const gwbSheath =
    state.sheathingType === 'gwb' ? INTERIOR_GWB_RSI_PER_MM * parseFloat(state.gwbSheathThickness || '0') : 0;

  const codebordSheath =
    state.sheathingType === 'codebord' ? CODEBORD_RSI_PER_INCH * parseFloat(state.codebordSheathThickness || '0') : 0;

  return osb + gwbSheath + codebordSheath;
};

// --- “Steel stud RSI” used by the sheet (very small)
const steelStudRSIConstant = 0.0000161;
const steelStudRSI = steelSize => {
  switch (steelSize) {
    case '2x4':
      return steelStudRSIConstant * 92;
    case '2x6':
      return steelStudRSIConstant * 152;
    case '2x8':
      return steelStudRSIConstant * 203;
    case '2x10':
      return steelStudRSIConstant * 254;
    default:
      return 0;
  }
};

export const calcSteelK = (state, t, locale) => {
  // Percentages (NBCC method)
  const AfPct = structurePercent(state.steelSpacingGroup); // Structure %
  const AcPct = 100 - AfPct; // Cavity %

  // Layer pieces used by sheet
  const extFilm = AIR_FILM_EXTERIOR_RSI;
  const claddingRSI = CLADDING_RSI[state.cladding] ?? 0;
  const rainScreenRSI = state.hasRainScreen ? RAINSCREEN_RSI[state.rainScreenAirGap] ?? 0 : 0;
  const sheathRSI = sheathingRSI(state);
  const hasExtInsulation = state.sheathingType !== 'codebord' && state.hasExteriorInsulation === true;
  let exteriorInsulationRSI = 0;
  if (hasExtInsulation) {
    if (state.exteriorInsulationType === 'rainbarrier') {
      exteriorInsulationRSI = roundOff(RAINBARRIER_RSI_PER_INCH * parseFloat(state.exteriorRainbarrierInches || '0'));
    } else {
      exteriorInsulationRSI = roundOff(CODEBORD_RSI_PER_INCH * parseFloat(state.exteriorCodebordInches || '0'));
    }
  }
  const interiorGwbRSI = roundOff(INTERIOR_GWB_RSI_PER_MM * parseFloat(state.interiorGWB || '0'));
  const strapBehindGwbRSI = state.woodStrapBehindGWB ? 0.16 : 0;
  const intFilm = AIR_FILM_INTERIOR_RSI;

  const studRSI = steelStudRSI(state.steelSize);
  const cavityRSI = parseFloat(state.cavityRSISteel || '0');

  // RSIf and RSIc (H10/H11)
  const RSIf =
    extFilm +
    claddingRSI +
    rainScreenRSI +
    sheathRSI +
    exteriorInsulationRSI +
    studRSI +
    interiorGwbRSI +
    strapBehindGwbRSI +
    intFilm;

  const RSIc =
    extFilm +
    claddingRSI +
    rainScreenRSI +
    sheathRSI +
    exteriorInsulationRSI +
    cavityRSI +
    interiorGwbRSI +
    strapBehindGwbRSI +
    intFilm;

  // RSI t1 / t2 / t3 (H12, H13, H14)
  const RSIt1 = 100 / (AfPct / RSIf + AcPct / RSIc);
  const RSIt2 = 100 / (AfPct / studRSI + AcPct / cavityRSI);
  const RSIt3 =
    extFilm +
    claddingRSI +
    rainScreenRSI +
    sheathRSI +
    exteriorInsulationRSI +
    RSIt2 +
    interiorGwbRSI +
    strapBehindGwbRSI +
    intFilm;

  // ---------- Outboard / Inboard (around first low-perm foam layer) ----------
  const lowPermAtExterior =
    state.sheathingType !== 'codebord' &&
    state.hasExteriorInsulation &&
    state.exteriorInsulationType === 'codebord' &&
    parseFloat(state.exteriorCodebordInches || '0') > 0;

  let outboardRSI = AIR_FILM_EXTERIOR_RSI + claddingRSI + rainScreenRSI;
  if (state.sheathingType === 'codebord') {
    outboardRSI += sheathRSI;
  } else {
    outboardRSI += exteriorInsulationRSI;
  }

  // Everything inside the foam (foam excluded)
  let inboardContinuousRSI = cavityRSI + interiorGwbRSI + strapBehindGwbRSI + AIR_FILM_INTERIOR_RSI;
  if (state.sheathingType !== 'codebord') {
    inboardContinuousRSI += sheathRSI;
  }

  const outboardInboardRatio =
    state.sheathingType !== 'codebord' && inboardContinuousRSI > 0
      ? roundOff(outboardRSI / inboardContinuousRSI)
      : null;

  // K1 / K2 and effective
  const K1 = pickK1(state);
  const K2 = 1 - K1;
  const RSIeff = K1 * RSIt1 + K2 * RSIt3;

  const steelSpacingGroupOptions = getSteelSpacingGroupOptions({ t });
  const spacingLabel = steelSpacingGroupOptions.find(s => s.value === state.steelSpacingGroup)?.label || '';
  // Replace ≥ with >= for better compatibility when generating pdfs
  const spacingLabelSafe = spacingLabel.replace(/≥/g, '>=');

  const installedInsulationRSI =
    state.sheathingType === 'codebord' ? cavityRSI + sheathRSI : cavityRSI + exteriorInsulationRSI;

  const result = {
    AfPercent: AfPct,
    AcPercent: AcPct,
    RSIf,
    RSIc,
    RSIt1,
    RSIt2,
    RSIt3,
    K1,
    K2,
    effectiveAssemblyRSI: RSIeff,
    centerOfCavityRSI: RSIc,
    installedInsulationRSI,
    rainScreenRSI: state.hasRainScreen ? RAINSCREEN_RSI[state.rainScreenAirGap] ?? 0 : 0,
    brickAirSpaceRSI:
      state.cladding === 'brickClay102' ? BRICK_AIR_SPACE_RSI[state.hasBrickAirSpace ? 'yes' : 'no'] ?? 0 : 0,
    sheathingRSI: sheathRSI,
    interiorGwbRSI,
    exteriorInsulationRSI,
    lowPermAtExterior,
    outboardInboardRatio,
  };

  const framingText = getFramingText({ t });

  // NEW: normalized ledger
  result.layers = {
    continuous: layerListFromInputsSteel(state, result, t, locale),
    nonContinuous: [
      {
        id: 'stud_size',
        label: `${t('thermal_calculator.walls_above_grade.steel_studs')} ${
          framingText[state.steelSize]
        }, ${spacingLabel} ${t('thermal_calculator.floors_over_unheated_spaces.on_center')}`,
        labelSafe: `${t('thermal_calculator.walls_above_grade.steel_studs')} ${
          framingText[state.steelSize]
        }, ${spacingLabelSafe} ${t('thermal_calculator.floors_over_unheated_spaces.on_center')}`,
        rsi: RSIf,
      },
      {
        id: 'stud_cavity',
        label: `RSI ${Number(cavityRSI).toFixed(2)}, R ${toR(cavityRSI).toFixed(0)} ${t(
          'thermal_calculator.floors_over_unheated_spaces.nominal_insulation'
        )}`,
        rsi: cavityRSI,
      },
    ],
  };
  return result;
};
